import { css } from '@emotion/react';
import { faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import {
  memo, useState,
} from 'react';
import {
  Accordion, AccordionHeaderSize,
} from '~/_shared/baseComponents/accordion';
import {
  ButtonComponent, ButtonSize,
  ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTranslation } from '~/_shared/utils/hooks';
import type {
  Place, PlaceId,
} from '~/places/places.types';
import type { PlacesSearchId } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { PlacesListingHeaderComponent } from './placesListingHeader.component';
import { PlacesListingPlaceDetailsComponent } from './placesListingPlaceDetails.component';
import type { PlaceSearchListingItem } from './placesTool.container';
import { getPlacesMarkerProps } from './placesTool.helpers';
import { placesSearchItemPanelStyle } from './placesTool.styles';

const loadMoreContainerStyle = css({
  position: 'relative',
  display: 'flex',
  padding: '8px 38px',
  justifyContent: 'center',
  alignItems: 'center',
});

const PlacesToolListingComponent = ({
  expandedSearches,
  searches,
  onPlaceHover,
  onPlaceHoverStopped,
  onToggleSearch,
  onSearchHovered,
  onSearchHoverStopped,
}: {
  expandedSearches: ReadonlySet<PlaceId>;
  searches: ReadonlyArray<PlaceSearchListingItem>;
  onPlaceHover: (place: Place, search: PlacesSearchId) => void;
  onPlaceHoverStopped: () => void;
  onToggleSearch: (searchId: PlaceId) => void;
  onSearchHovered: (searchId: PlacesSearchId) => void;
  onSearchHoverStopped: () => void;
}) => {
  const [t] = useTranslation();

  const [activeContextMenuSearchId, setActiveContextMenuSearchId] = useState<null | PlacesSearchId>(null);

  const getPlaceContextMenuProps = ((placeSearchId: PlacesSearchId) => ({
    isContextMenuVisible: activeContextMenuSearchId === placeSearchId,
    onHideContextMenu: () => setActiveContextMenuSearchId(null),
    onOpenContextMenu: () => setActiveContextMenuSearchId(placeSearchId),
  }));

  return (
    <Accordion>
      {searches.map(search => {
        const markerProps = getPlacesMarkerProps({ placeColor: search.color });

        return (
          <Accordion.Item
            key={search.id}
            onMouseEnter={() => onSearchHovered(search.id)}
            onMouseLeave={onSearchHoverStopped}
            open={expandedSearches.has(search.id)}
            onToggle={() => onToggleSearch(search.id)}
          >
            <Accordion.ItemHeading
              size={AccordionHeaderSize.Flexible}
            >
              <PlacesListingHeaderComponent
                markerVisualizerProps={markerProps}
                contextMenuProps={getPlaceContextMenuProps(search.id)}
                place={search}
              />
            </Accordion.ItemHeading>
            <Accordion.Panel
              css={placesSearchItemPanelStyle}
            >
              {search.items.map((item, index) => {
                return (
                  <PlacesListingPlaceDetailsComponent
                    key={item.id}
                    place={item}
                    index={index}
                    color={search.color}
                    onHover={() => onPlaceHover(item, search.id)}
                    onHoverStopped={onPlaceHoverStopped}
                  />
                );
              })}
              {search.onLoadMore && (
                <div
                  css={loadMoreContainerStyle}
                >
                  {search.isLoading && <OverlayLoaderComponent loaderSize={24} />}
                  <ButtonComponent
                    buttonStyle={ButtonStyle.Quaternary}
                    size={ButtonSize.Small}
                    prefixIcon={faArrowDown}
                    text={t('Load More...')}
                    onClick={search.onLoadMore}
                    isDisabled={search.isLoading}
                  />
                </div>
              )}
            </Accordion.Panel>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};

const pureComponent = memo(PlacesToolListingComponent);
export { pureComponent as PlacesToolListingComponent };
